/**
 * Bean Snake client: start/submit/board/report against /api/arcade
 * (?action=… routing, same shape as square-service.ts), plus an offline
 * submit queue.
 *
 * Why the queue exists: a ranked run spends its "try" when it STARTS, not
 * when it submits. A player who finishes a great run and then loses
 * connectivity — or draws a transient 429/5xx — has already paid for that
 * try. Losing the submission would mean losing the score for nothing, so
 * anything short of a definitive server verdict is parked in localStorage
 * and retried on next open via flushQueuedRuns.
 */
import type { InputEvent } from '@shared/arcade/types';
import { apiFetch, ApiError, getSessionId } from './api-client';

/**
 * Per-address, NOT global. A single flat key is shared by every account on a
 * device: if A queues a run offline and B logs in before it flushes, B's session
 * retries A's run, gets 403 forever, and A's paid-for run is unrecoverable —
 * B can never "become" A by retrying. Namespacing matches api-client.ts's own
 * sessionKey(addr) precedent.
 */
export function queueKey(address: string): string {
  return `bitbrew.arcade.pending.${address}`;
}

export interface QueuedRun {
  runId: string;
  inputs: InputEvent[];
  clientScore: number;
}

/** Guards against a syntactically-valid-JSON but semantically-broken entry
 *  (e.g. a missing runId) making it out of storage and into a POST that can
 *  only ever draw a 400. Cheaper to drop here than to pay the round trip. */
function isQueuedRunShape(x: unknown): x is QueuedRun {
  if (!x || typeof x !== 'object') return false;
  const r = x as Record<string, unknown>;
  return typeof r.runId === 'string' && Array.isArray(r.inputs);
}

/** Terminal server verdicts: the answer cannot change on retry, so a queued run
 *  carrying one must be dropped rather than retried forever.
 *  - 403 never self-heals (the address check is session-derived)
 *  - 404/409 mean the run is gone or already claimed
 *  - 422 is provably terminal: the row transitioned off 'issued' in the same
 *    request that returned it, so a retry would only draw a 409 anyway
 *  - 400 means a malformed entry that will never parse
 * Everything else — network failure, 429, 5xx — may still have the row sitting
 * at 'issued' server-side, so it stays queued and is retried. */
export const TERMINAL_STATUSES = [400, 403, 404, 409, 422];

export interface BoardRow {
  rank: number;
  address: string;
  name: string;
  avatarUrl: string | null;
  score: number;
  isViewer: boolean;
}

// ─── Offline submit queue ──────────────────────────────

/** Corrupt storage must never take the arcade down on open — a bad blob is
 *  treated as an empty queue, which loses at most one unsent run. */
export function readQueue(address: string): QueuedRun[] {
  try {
    const raw = localStorage.getItem(queueKey(address));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isQueuedRunShape) : [];
  } catch {
    return [];
  }
}

export function queueRun(address: string, run: QueuedRun): void {
  const queue = readQueue(address);
  queue.push(run);
  try { localStorage.setItem(queueKey(address), JSON.stringify(queue)); } catch { /* quota — drop */ }
}

export function clearQueue(address: string): void {
  try { localStorage.removeItem(queueKey(address)); } catch { /* ignore */ }
}

function writeQueue(address: string, queue: QueuedRun[]): void {
  try {
    if (queue.length === 0) clearQueue(address);
    else localStorage.setItem(queueKey(address), JSON.stringify(queue));
  } catch { /* quota — best effort, next flush retries from whatever is stored */ }
}

// ─── Network ────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const sessionId = getSessionId();
  return sessionId ? { Authorization: `Bearer ${sessionId}` } : {};
}

export async function startRun(): Promise<{
  runId: string; seed: number; engineVersion: number; triesAvailable: number;
}> {
  return apiFetch('/api/arcade?action=start', {});
}

/**
 * The invariant is: a finished ranked run must never be lost — its try was
 * already spent when the run STARTED. So anything short of a definitive,
 * terminal server verdict (see TERMINAL_STATUSES) gets queued rather than
 * thrown at a caller that might not independently catch and retry it. That
 * covers a network failure (fetch throws something that isn't an ApiError)
 * AND a non-terminal ApiError (429 rate-limited, any 5xx) — the run row may
 * still be sitting at 'issued' server-side in both cases, so it's still
 * recoverable. Only a terminal status is rethrown, since the server has
 * given an answer that cannot change on retry.
 */
export async function submitRun(
  address: string,
  runId: string,
  inputs: InputEvent[],
  clientScore: number,
): Promise<{ score: number } | { queued: true }> {
  try {
    return await apiFetch<{ score: number }>('/api/arcade?action=submit', {
      runId, inputs, clientScore,
    });
  } catch (err) {
    if (err instanceof ApiError && TERMINAL_STATUSES.includes(err.status)) throw err;
    queueRun(address, { runId, inputs, clientScore });
    return { queued: true };
  }
}

export async function fetchBoard(
  scope: 'global' | 'shop',
  shopId?: string,
): Promise<{ week: string; rows: BoardRow[]; viewer: BoardRow | null; triesAvailable: number }> {
  const params = new URLSearchParams({ action: 'board', scope });
  if (scope === 'shop' && shopId) params.set('shopId', shopId);
  const res = await fetch(`/api/arcade?${params.toString()}`, { headers: authHeaders() });
  if (!res.ok) {
    let message = `Board fetch failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch { /* ignore */ }
    throw new ApiError(res.status, message);
  }
  return res.json();
}

export async function reportPlayer(address: string): Promise<void> {
  await apiFetch('/api/arcade?action=report', { reported: address });
}

export interface FlushResult {
  sent: number;
  // Runs dropped specifically because the server said `run_expired` (a
  // queued run flushed more than 30 minutes after it STARTED) — distinct
  // from every other terminal drop (already_submitted, not_your_run, a
  // malformed entry). The module header's invariant is that a finished
  // ranked run must never be LOST; dropping an expired run is acceptable
  // (the server genuinely will not score it), but dropping it with zero
  // signal contradicts that invariant just as much as losing it would.
  // ArcadePanel surfaces this count so the player is told, rather than the
  // run just quietly disappearing. See I3 in the whole-branch review.
  expired: number;
}

/**
 * Re-POST every parked run for this address. A terminal status (see
 * TERMINAL_STATUSES) means the try is genuinely gone or the answer can never
 * change — those are dropped rather than kept, which is what stops an
 * unsendable run being retried forever. Anything else (still offline, a
 * 429, a 5xx) stays queued for the next flush. One pass over the queue, no
 * internal retrying, so this always terminates.
 */
export async function flushQueuedRuns(address: string): Promise<FlushResult> {
  const queue = readQueue(address);
  if (queue.length === 0) return { sent: 0, expired: 0 };

  let sent = 0;
  let expired = 0;
  const remaining: QueuedRun[] = [];

  for (const run of queue) {
    try {
      await apiFetch('/api/arcade?action=submit', {
        runId: run.runId, inputs: run.inputs, clientScore: run.clientScore,
      });
      sent++;
    } catch (err) {
      if (err instanceof ApiError && TERMINAL_STATUSES.includes(err.status)) {
        // `body` is the parsed JSON error payload (api-client.ts's ApiError
        // now carries it) — `error: 'run_expired'` is how handleSubmit
        // (api/arcade.ts) reports this specific terminal drop.
        const body = err.body as { error?: string } | undefined;
        if (body?.error === 'run_expired') expired++;
        continue; // terminal verdict — retrying can't change the answer
      }
      remaining.push(run);
    }
  }

  writeQueue(address, remaining);
  return { sent, expired };
}
